import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  BuilderName,
  InquiryTemplate,
  InquiryTemplateStatus,
} from 'dashboard/models/inquiry-template';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  dynamicFlowTemplatesOnlySelector,
  getAllTemplates,
} from 'dashboard/redux/selectors/inquiry-template';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/slices/inquiry-templates';
import { RootState } from 'dashboard/redux/store';
import {
  cloneInquiryTemplate,
  createInquiryTemplate,
  getEditorPath,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import {
  loadInquiryTemplateBuilders,
  loadInquiryTemplateFiltersList,
  loadInquiryTemplatesList,
  loadTemplateBuilderPreview,
} from 'dashboard/redux/thunk-actions/inquiry-templates';
import {
  openInquiryTemplateVersionPreview,
  previewInquiryFlow,
} from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import DashboardInquiryTemplatesView from 'dashboard/views/DashboardInquiryTemplatesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import InquiryParams from 'verify/lib/interfaces/InquiryParams';

const DashboardInquiryTemplatesRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadInquiryTemplatesList,
      props.currentPage,
      props.filters,
      props.pageSize,
      props.sortParams,
    ],
  );

  const { loading: loadingTemplates, callback: inquiryTemplateLoader } = useApiLoader({
    loader: async () =>
      props.loadInquiryTemplatesList({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        abortSignal: abortController.signal,
      }),
    deps: [
      props.loadInquiryTemplatesList,
      props.currentPage,
      props.filters,
      props.pageSize,
      props.sortParams,
    ],
    onError: (error) => {
      // Skip permissions error since it is handled by template filters
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    abortController: abortController,
  });

  const { loading: loadingTemplateFilters } = useApiLoader({
    loader: async () =>
      props.loadInquiryTemplateFiltersList({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
      }),
    deps: [props.loadInquiryTemplateFiltersList, props.filters],
    onError: (_) => {
      props.showMessage('Failed to load template filters.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: async () => props.loadInquiryTemplateBuilders(),
    deps: [props.loadInquiryTemplateBuilders],
    onError: () => null,
    onNetworkError: () => null,
  });

  const {
    callback: handleLoadTemplateBuilderPreview,
    error: handleLoadTemplateBuilderPreviewError,
  } = useApiCallback(
    async (inquiryTemplateBuilderName: BuilderName) => {
      await props.loadTemplateBuilderPreview(inquiryTemplateBuilderName);
    },
    [props.loadTemplateBuilderPreview],
  );

  const {
    callback: handleCreateInquiryTemplate,
    loading: handleCreateInquiryTemplateLoading,
    error: handleCreateInquiryTemplateError,
  } = useApiCallback(
    async (inquiryTemplateBuilderName: string) => {
      const { inquiryTemplate, inquiryTemplateVersion } = await props.createInquiryTemplate(
        inquiryTemplateBuilderName,
      );
      props.showMessage('Your template was successfully created.', MessageType.Success);
      navigate(getEditorPath(inquiryTemplate.id, inquiryTemplateVersion.id, 'flow'));
    },
    [props.createInquiryTemplate],
  );

  const {
    error: handleActivateInquiryTemplateError,
    loading: handleActivateInquiryTemplateLoading,
    callback: handleActivateInquiryTemplate,
  } = useApiCallback(
    async (inquiryTemplateId: string) => {
      await SessionClientDashboard.postResourceAction<InquiryTemplate>(
        { id: inquiryTemplateId, type: ApiModel.InquiryTemplate },
        'activate',
      );
      inquiryTemplateLoader();
      props.showMessage('Successfully activated template.', MessageType.Success);
    },
    [inquiryTemplateLoader],
  );

  const {
    error: handleDeactivateInquiryTemplateError,
    loading: handleDeactivateInquiryTemplateLoading,
    callback: handleDeactivateInquiryTemplate,
  } = useApiCallback(
    async (inquiryTemplateId: string) => {
      await SessionClientDashboard.postResourceAction<InquiryTemplate>(
        { id: inquiryTemplateId, type: ApiModel.InquiryTemplate },
        'deactivate',
      );
      inquiryTemplateLoader();
      props.showMessage('Successfully deactivated template.', MessageType.Success);
    },
    [inquiryTemplateLoader],
  );

  const {
    error: handleCloneInquiryTemplateError,
    loading: handleCloneInquiryTemplateLoading,
    callback: handleCloneInquiryTemplate,
  } = useApiCallback(async (inquiryTemplateId: string, name: string | undefined) => {
    props.showMessage(
      'Making a copy of your template, this may take a minute...',
      MessageType.Notice,
    );
    const { inquiryTemplate, inquiryTemplateVersion } = await props.cloneInquiryTemplate(
      inquiryTemplateId,
      name,
    );
    props.showMessage('Successfully made a copy of your template.', MessageType.Success);
    navigate(getEditorPath(inquiryTemplate.id, inquiryTemplateVersion.id, 'flow'));
  }, []);

  useApiErrorHandler({
    error: handleLoadTemplateBuilderPreviewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load template preview. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: handleCreateInquiryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403 && error.errors?.[0].code === 'active_template_limit_error') {
        props.showMessage(
          'Your current plan only supports five active templates. Please contact our team if you’d like to learn more.',
          MessageType.Notice,
        );
      } else {
        props.showMessage('Failed to create template. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: handleCloneInquiryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403 && error.errors?.[0].code === 'active_template_limit_error') {
        props.showMessage(
          'Your current plan only supports five active templates. Please contact our team if you’d like to learn more.',
          MessageType.Notice,
        );
      } else {
        props.showMessage(
          'Failed to make a copy of your template. Please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: handleDeactivateInquiryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to deactivate template. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: handleActivateInquiryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403 && error.errors?.[0].code === 'active_template_limit_error') {
        props.showMessage(
          'Your current plan only supports five active templates. Please contact our team if you’d like to learn more.',
          MessageType.Notice,
        );
      } else {
        props.showMessage('Failed to activate template. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleStatusFilterUpdate = (statuses: InquiryTemplateStatus[]) => {
    props.setFilters({ filters: { status: statuses } });
    handlePageLinkClick(1);
  };

  const handlePreviewInquiry = (
    inquiryTemplateId: string,
    inquiryTemplateVersionId: string,
    environmentName?: string,
  ) => {
    openInquiryTemplateVersionPreview({
      inquiryTemplateId,
      inquiryTemplateVersionId,
      environmentName,
    });
  };

  const handlePreviewLegacyInquiry = (templateId: string) => {
    const params: InquiryParams = {
      environment: 'sandbox',
      'template-id': templateId,
    };
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    previewInquiryFlow(params, location, props.organization.attributes.subdomain);
  };

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch, handleSortUpdate } = useTable(
    {
      filters: props.filters,
      sortParams: props.sortParams,
      pageSize: props.pageSize,
      currentPage: props.currentPage,
      setFilters: (filters) => props.setFilters({ filters }),
      setSortParams: (sortParams) => props.setSortParams({ sortParams }),
      setCurrentPage: (currentPage) => props.setCurrentPage({ currentPage }),
      setPageSize: (pageSize) => props.setPageSize({ pageSize }),
    },
  );

  const loading =
    loadingTemplates ||
    loadingTemplateFilters ||
    handleActivateInquiryTemplateLoading ||
    handleDeactivateInquiryTemplateLoading ||
    handleCreateInquiryTemplateLoading;

  return (
    <DashboardInquiryTemplatesView
      inquiryTemplates={props.allTemplates}
      legacyTemplateFilters={props.legacyTemplateFilters}
      inquiryTemplateFilters={props.inquiryTemplateFilters}
      inquiryTemplateBuilders={props.inquiryTemplateBuilders}
      builderPreviews={props.builderPreviews}
      isLoading={loading}
      isCloneLoading={handleCloneInquiryTemplateLoading}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      currentEnvironmentType={props.currentEnvironment.type}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadTemplateBuilderPreview={handleLoadTemplateBuilderPreview}
      handleCreateInquiryTemplate={handleCreateInquiryTemplate}
      handlePreviewInquiry={handlePreviewInquiry}
      handlePreviewLegacyInquiry={handlePreviewLegacyInquiry}
      handleActivateInquiryTemplate={handleActivateInquiryTemplate}
      handleDeactivateInquiryTemplate={handleDeactivateInquiryTemplate}
      handleCloneInquiryTemplate={handleCloneInquiryTemplate}
      dynamicFlowTemplatesOnly={props.dynamicFlowTemplatesOnly}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const dynamicFlowTemplatesOnly = dynamicFlowTemplatesOnlySelector(state);
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    organization: state.organization.organization,
    allTemplates: dynamicFlowTemplatesOnly
      ? state.inquiryTemplates.inquiryTemplates
      : getAllTemplates(state),
    legacyTemplateFilters: dynamicFlowTemplatesOnly
      ? []
      : state.inquiryTemplates.legacyTemplateFilters,
    inquiryTemplateFilters: state.inquiryTemplates.inquiryTemplateFilters,
    inquiryTemplateBuilders: state.inquiryTemplates.inquiryTemplateBuilders,
    builderPreviews: state.inquiryTemplates.templateBuilderPreviews,
    filters: state.inquiryTemplates.filters,
    sortParams: state.inquiryTemplates.sortParams,
    paginationParams: state.inquiryTemplates.paginationParams,
    pageSize: state.inquiryTemplates.pageSize,
    currentPage: state.inquiryTemplates.currentPage,
    dynamicFlowTemplatesOnly,
  };
};

const mapDispatchToProps = {
  loadInquiryTemplatesList,
  loadInquiryTemplateFiltersList,
  loadInquiryTemplateBuilders,
  loadTemplateBuilderPreview,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
  showMessage,
  createInquiryTemplate,
  cloneInquiryTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryTemplatesRoute);
